import { useId } from 'react';
import { brand } from '../config/brand';

interface LogoProps {
  /** `full` shows the descriptor line; `compact` is the wordmark only. */
  variant?: 'full' | 'compact';
  /** Text tone for the surface it sits on. */
  tone?: 'dark' | 'light';
  /** Icon size in px (height/width of the badge). */
  iconSize?: number;
  className?: string;
}

/**
 * Uclipse brand lockup: badge mark + "UCLIPSE" wordmark + optional descriptor.
 * Reused in the header, footer and share card so the identity stays consistent.
 */
export default function Logo({
  variant = 'full',
  tone = 'dark',
  iconSize = 38,
  className = '',
}: LogoProps) {
  const gradId = `logo-${useId().replace(/:/g, '')}`;
  const wordmark = tone === 'light' ? 'text-white' : 'text-brand-700';
  const descriptor = tone === 'light' ? 'text-white/70' : 'text-slate-500';

  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 40 40"
        fill="none"
        role="img"
        aria-label={`${brand.name} logo`}
        className="shrink-0 drop-shadow-sm"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
            <stop stopColor="#0E5BA8" />
            <stop offset="1" stopColor="#3787D3" />
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="11" fill={`url(#${gradId})`} />
        {/* Stylized U */}
        <path
          d="M12 11v11.2a8 8 0 0 0 16 0V11h-4.4v11.2a3.6 3.6 0 0 1-7.2 0V11H12Z"
          fill="#fff"
        />
        {/* Green spark echoing the brand star */}
        <path
          d="M29.5 9.2c.2 1.7.9 2.4 2.6 2.6-1.7.2-2.4.9-2.6 2.6-.2-1.7-.9-2.4-2.6-2.6 1.7-.2 2.4-.9 2.6-2.6Z"
          fill="#7BC142"
        />
        {/* Green base accent */}
        <rect x="12" y="30.5" width="16" height="3" rx="1.5" fill="#7BC142" />
      </svg>
      <span className="flex flex-col leading-none">
        <span className={`font-display text-xl font-bold tracking-tight ${wordmark}`}>
          {brand.name.toUpperCase()}
        </span>
        {variant === 'full' && (
          <span className={`mt-1 text-[0.6rem] font-semibold uppercase tracking-[0.18em] ${descriptor}`}>
            {brand.descriptor}
          </span>
        )}
      </span>
    </span>
  );
}
